import React, { useState, memo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Toggle } from '../ui/Toggle';
import { Card, CardHeader } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, AlertTriangleIcon, CopyIcon, EyeIcon } from 'lucide-react';
type WizardStep = 'source' | 'sandbox' | 'auth' | 'confirm';
const steps: {
  id: WizardStep;
  label: string;
}[] = [{
  id: 'source',
  label: 'Source'
}, {
  id: 'sandbox',
  label: 'Runtime Sandbox'
}, {
  id: 'auth',
  label: 'Authentication'
}, {
  id: 'confirm',
  label: 'Confirmation'
}];
export function AddServer() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<WizardStep>('source');
  const [formData, setFormData] = useState({
    name: '',
    containerImage: '',
    mcpPort: '3000',
    cpu: '0.5',
    memory: '512',
    timeout: '30',
    networkEgress: false,
    filesystemMode: 'readonly',
    authMode: 'static',
    token: ''
  });
  const [generatedToken, setGeneratedToken] = useState('');
  const [tokenRevealed, setTokenRevealed] = useState(false);
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const currentStepIndex = steps.findIndex(s => s.id === currentStep);
  const handleNext = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      setCurrentStep(steps[nextIndex].id);
    }
  };
  const handleBack = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex].id);
    }
  };
  const handleGenerateToken = () => {
    const token = 'mcp_' + Array.from({
      length: 32
    }, () => 'abcdefghijklmnopqrstuvwxyz0123456789'[Math.floor(Math.random() * 36)]).join('');
    setGeneratedToken(token);
    setTokenRevealed(true);
  };
  const handleTestConnection = () => {
    setTestStatus('testing');
    setTimeout(() => {
      setTestStatus('success');
    }, 1500);
  };
  const handleCreate = () => {
    navigate('/');
  };
  const generatedEndpoint = `https://mcp.mcpguard.io/v1/${formData.name.toLowerCase().replace(/\s+/g, '-') || 'server'}`;
  return <div className="max-w-2xl mx-auto" data-id="element-147">
      {/* Back Link */}
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 mb-6" data-id="element-148">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-149" />
        Back to Servers
      </Link>

      {/* Header */}
      <div className="mb-6" data-id="element-150">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100" data-id="element-151">
          Add Server
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5" data-id="element-152">
          Onboard a new MCP server with secure defaults
        </p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center gap-2 mb-8" data-id="element-153">
        {steps.map((step, index) => <div key={step.id} className="flex items-center" data-id="element-154">
            <div className={`
                flex items-center justify-center w-7 h-7 rounded-full text-xs font-medium
                ${index < currentStepIndex ? 'bg-blue-600 text-white' : index === currentStepIndex ? 'bg-blue-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'}
              `} data-id="element-155">
              {index < currentStepIndex ? <CheckIcon className="h-4 w-4" data-id="element-156" /> : index + 1}
            </div>
            <span className={`ml-2 text-sm ${index === currentStepIndex ? 'text-slate-900 dark:text-slate-100 font-medium' : 'text-slate-500 dark:text-slate-400'}`} data-id="element-157">
              {step.label}
            </span>
            {index < steps.length - 1 && <div className="w-8 h-px bg-slate-200 dark:bg-slate-700 mx-3" data-id="element-158" />}
          </div>)}
      </div>

      {/* Step Content */}
      <Card data-id="element-159">
        {currentStep === 'source' && <div className="space-y-4" data-id="element-160">
            <CardHeader title="Source Configuration" description="Specify the container image and MCP port" data-id="element-161" />
            <Input label="Server Name" placeholder="Production API Gateway" value={formData.name} onChange={e => setFormData({
          ...formData,
          name: e.target.value
        })} data-id="element-162" />
            <Input label="Container Image" placeholder="ghcr.io/org/mcp-server:latest" value={formData.containerImage} onChange={e => setFormData({
          ...formData,
          containerImage: e.target.value
        })} monospace hint="Docker image URL for your MCP server" data-id="element-163" />
            <Input label="MCP Port" type="number" placeholder="3000" value={formData.mcpPort} onChange={e => setFormData({
          ...formData,
          mcpPort: e.target.value
        })} hint="Port your MCP server listens on" data-id="element-164" />
          </div>}

        {currentStep === 'sandbox' && <div className="space-y-4" data-id="element-165">
            <CardHeader title="Runtime Sandbox" description="Configure resource limits and isolation" data-id="element-166" />
            <div className="grid grid-cols-2 gap-4" data-id="element-167">
              <Select label="CPU Allocation" options={[{
            value: '0.25',
            label: '0.25 vCPU'
          }, {
            value: '0.5',
            label: '0.5 vCPU'
          }, {
            value: '1',
            label: '1 vCPU'
          }, {
            value: '2',
            label: '2 vCPU'
          }]} value={formData.cpu} onChange={e => setFormData({
            ...formData,
            cpu: e.target.value
          })} data-id="element-168" />
              <Select label="Memory" options={[{
            value: '256',
            label: '256 MB'
          }, {
            value: '512',
            label: '512 MB'
          }, {
            value: '1024',
            label: '1 GB'
          }, {
            value: '2048',
            label: '2 GB'
          }]} value={formData.memory} onChange={e => setFormData({
            ...formData,
            memory: e.target.value
          })} data-id="element-169" />
            </div>
            <Select label="Execution Timeout" options={[{
          value: '10',
          label: '10 seconds'
        }, {
          value: '30',
          label: '30 seconds'
        }, {
          value: '60',
          label: '60 seconds'
        }, {
          value: '120',
          label: '2 minutes'
        }]} value={formData.timeout} onChange={e => setFormData({
          ...formData,
          timeout: e.target.value
        })} data-id="element-170" />
            <div className="pt-2 space-y-4" data-id="element-171">
              <Toggle label="Network Egress" description="Allow outbound network connections" checked={formData.networkEgress} onChange={checked => setFormData({
            ...formData,
            networkEgress: checked
          })} danger data-id="element-172" />
              {formData.networkEgress && <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/30 rounded-md" data-id="element-173">
                  <AlertTriangleIcon className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" data-id="element-174" />
                  <p className="text-xs text-amber-800 dark:text-amber-300" data-id="element-175">
                    Enabling network egress increases attack surface. Configure
                    egress allowlists in the Policy Builder after creation.
                  </p>
                </div>}
              <Select label="Filesystem Mode" options={[{
            value: 'readonly',
            label: 'Read-only (recommended)'
          }, {
            value: 'readwrite',
            label: 'Read-write'
          }]} value={formData.filesystemMode} onChange={e => setFormData({
            ...formData,
            filesystemMode: e.target.value
          })} data-id="element-176" />
            </div>
          </div>}

        {currentStep === 'auth' && <div className="space-y-4" data-id="element-177">
            <CardHeader title="Authentication" description="Configure how clients authenticate to this server" data-id="element-178" />
            <Select label="Authentication Mode" options={[{
          value: 'static',
          label: 'Static Token'
        }, {
          value: 'oauth',
          label: 'OAuth Bearer Token'
        }]} value={formData.authMode} onChange={e => setFormData({
          ...formData,
          authMode: e.target.value
        })} data-id="element-179" />
            {formData.authMode === 'static' && <div className="space-y-3" data-id="element-180">
                {!generatedToken ? <Button variant="secondary" onClick={handleGenerateToken} data-id="element-181">
                    Generate Token
                  </Button> : <div className="space-y-2" data-id="element-182">
                    <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/30 rounded-md" data-id="element-183">
                      <AlertTriangleIcon className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" data-id="element-184" />
                      <p className="text-xs text-amber-800 dark:text-amber-300" data-id="element-185">
                        This token will only be shown once. Copy it now and
                        store it securely.
                      </p>
                    </div>
                    <Input label="Authentication Token" value={generatedToken} readOnly monospace copyable revealable data-id="element-186" />
                  </div>}
              </div>}
            {formData.authMode === 'oauth' && <p className="text-sm text-slate-500 dark:text-slate-400" data-id="element-187">
                OAuth configuration will be available in Settings after server
                creation.
              </p>}
          </div>}

        {currentStep === 'confirm' && <div className="space-y-4" data-id="element-188">
            <CardHeader title="Confirmation" description="Review your configuration and test the connection" data-id="element-189" />

            <div className="space-y-3" data-id="element-190">
              <div data-id="element-191">
                <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider" data-id="element-192">
                  MCP Endpoint URL
                </label>
                <div className="mt-1" data-id="element-193">
                  <Input value={generatedEndpoint} readOnly monospace copyable data-id="element-194" />
                </div>
              </div>

              {generatedToken && <div data-id="element-195">
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider" data-id="element-196">
                    Authentication Token
                  </label>
                  <div className="mt-1" data-id="element-197">
                    <Input value={generatedToken} readOnly monospace copyable revealable data-id="element-198" />
                  </div>
                </div>}

              <div className="pt-2" data-id="element-199">
                <Button variant="secondary" onClick={handleTestConnection} loading={testStatus === 'testing'} data-id="element-200">
                  {testStatus === 'success' ? <>
                      <CheckIcon className="h-4 w-4 mr-2 text-green-600 dark:text-green-500" data-id="element-201" />
                      Connection Successful
                    </> : 'Test Connection'}
                </Button>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100 dark:border-slate-800" data-id="element-202">
              <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-2" data-id="element-203">
                Configuration Summary
              </h4>
              <dl className="grid grid-cols-2 gap-2 text-xs" data-id="element-204">
                <dt className="text-slate-500 dark:text-slate-400" data-id="element-205">
                  Container Image
                </dt>
                <dd className="text-slate-900 dark:text-slate-100 font-mono" data-id="element-206">
                  {formData.containerImage || '—'}
                </dd>
                <dt className="text-slate-500 dark:text-slate-400" data-id="element-207">
                  Resources
                </dt>
                <dd className="text-slate-900 dark:text-slate-100" data-id="element-208">
                  {formData.cpu} vCPU, {formData.memory} MB
                </dd>
                <dt className="text-slate-500 dark:text-slate-400" data-id="element-209">
                  Network Egress
                </dt>
                <dd data-id="element-210">
                  <Badge variant={formData.networkEgress ? 'warning' : 'healthy'} data-id="element-211">
                    {formData.networkEgress ? 'Enabled' : 'Disabled'}
                  </Badge>
                </dd>
                <dt className="text-slate-500 dark:text-slate-400" data-id="element-212">
                  Filesystem
                </dt>
                <dd className="text-slate-900 dark:text-slate-100 capitalize" data-id="element-213">
                  {formData.filesystemMode}
                </dd>
              </dl>
            </div>
          </div>}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100 dark:border-slate-800" data-id="element-214">
          <Button variant="ghost" onClick={handleBack} disabled={currentStepIndex === 0} data-id="element-215">
            <ArrowLeftIcon className="h-4 w-4 mr-2" data-id="element-216" />
            Back
          </Button>
          {currentStep === 'confirm' ? <Button variant="primary" onClick={handleCreate} data-id="element-217">
              Create Server
            </Button> : <Button variant="primary" onClick={handleNext} data-id="element-218">
              Continue
              <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-219" />
            </Button>}
        </div>
      </Card>
    </div>;
}